import React from "react";
import PropTypes from "prop-types";
import { Container, Row, Col } from "@ui/wrapper";
import SectionTitle from "@ui/section-title";
import SwiperSlider, { SwiperSlide } from "@ui/swiper";
import Anchor from "@ui/anchor";
import Image from "@ui/image";
import { SectionTitleType, ItemType } from "@utils/types";
import {
    SectionWrap,
    Content,
    ContentBox,
    ContentBoxTitle,
    ContentBoxText,
    ContentImage,
    StyleHeading,
} from "./style";

const sliderSettings = {
    slidesPerView: 1,
};

const customDescription = [
    {
        title: "自动化控制",
        text: "SDM智能控制器能够自动化地监测和控制系统的运行状态。它集成了多种传感器和执行器，可以实时获取环境数据并根据用户定义的逻辑进行智能控制，实现自动化运行和调节。"
    }, {
        title: "智能优化",
        text: "该控制器基于机器学习和智能算法，能够对系统进行智能优化。通过学习和分析历史数据，它可以预测和优化系统的行为，提高效率、减少能耗，并根据实时数据进行实时调整，以达到最佳性能。"
    },
    {
        title: "远程监控与管理",
        text: "SDM智能控制器支持远程监控和管理功能。用户可以通过配套的移动应用或web界面，实时监视设备的状态、数据趋势和报警信息。远程管理功能使用户能够随时随地对设备进行配置、监控和控制，提高运维效率和故障处理能力。"
    },
    {
        title: "边缘计算能力",
        text: "该控制器具备边缘计算能力，可以在设备端进行实时数据处理和决策。通过将计算任务下放到设备端，可以降低对云平台的依赖，提高响应速度和降低通信成本，并增强系统的稳定性和安全性。"
    },
    {
        title: "OTA升级",
        text: "SDM智能控制器支持OTA（远程固件升级）功能，可以通过云平台进行远程固件更新。这样，用户可以及时获取最新的功能和安全修复，保持设备的性能和功能的最新状态，无需现场操作。"
    },
    {
        title: "云平台实时通信",
        text: "该控制器能够与云平台实时通信，实现设备与云端之间的数据交互和指令传递。它可以将实时数据、报警信息和设备状态传输到云端进行存储和分析，并接收云平台下发的指令和控制参数，实现远程控制和配置管理。"
    },
    {
        title: "可拖拽编程界面",
        text: "SDM智能控制器具备可拖拽编程界面，使用户能够通过简单的拖拽和连接方式构建控制逻辑。无需编写复杂的代码，用户可以自由定制和配置控制器的行为，实现个性化的控制方案。"
    }
]

const customLeftTable = [
    {
        label: "标准型CPU参数",
        value: "ARM Cortex-M7 / 400 MHz"
    },
    {
        label: "额外电压",
        value: "24VDC(20.4VDC到28.8VDC)"
    },
    {
        label: "外形尺寸 (mm)",
        value: "204 * 95 * 51"
    },
    {
        label: "本机数字量 In",
        value: "16输入：24V"
    },
    {
        label: "本机数字量 Out",
        value: "14输出：3A@250Vac/30Vdc"
    },
    {
        label: "本机模拟量 In",
        value: "2路：0-20mA"
    },
    {
        label: "本机模拟量 Out",
        value: "4路：0-5V"
    },
    {
        label: "物联网通讯",
        value: "4G / Ethernet"
    },
    {
        label: "物联网协议",
        value: "MQTT，HTTP，COAP，WebSocket"
    },
]

const customRightTable = [
    {
        label: "RS485-BUS",
        value: "Modbus / 1主 / 1从"
    },
    {
        label: "CAN-BUS",
        value: "1路 (可选)"
    },
    {
        label: "传感器接入种类",
        value: "液位，流量，流速，压力，电流，电压，振动等"
    },
    {
        label: "边缘计算",
        value: "基于机器流数据的实时数据处理分析"
    },
    {
        label: "逻辑控制",
        value: "泵、电机等设备本地控制、远程控制"
    },
    {
        label: "脉冲捕捉输入",
        value: "16"
    },
    {
        label: "存储卡",
        value: "microSD卡 (可选)"
    },
    {
        label: "实时时钟精度",
        value: "±120秒 / 月"
    },
    {
        label: "工作环境要求",
        value: "-20°-60°水平安装，-20°-50°垂直安装，相对湿度 95% 不结露；大气压 1139 至 795hPa (相当于海拔 -1000 到 2000m)"
    },
]

const ContentBoxArea = ({ data, layout }) => {
    return (
        <SectionWrap $layout={layout}>
            {data?.section_title && (
                <StyleHeading>
                    <h4>{data?.section_title.title}</h4>
                    <ContentBoxText>
                        {data?.section_title.subtitle}
                    </ContentBoxText>
                </StyleHeading>
            )}
            {data?.items.map((it, idx) => {
                if (idx % 2 === 0) {
                    return (
                        <ContentBox>
                            <Row>
                                <Col
                                    lg={{ span: 7, order: 1 }}
                                    xs={{ span: 12, order: 1 }}
                                >
                                    <Content>
                                        <ContentBoxTitle>
                                            {it.title}
                                        </ContentBoxTitle>
                                        <ContentBoxText>
                                            {it.description}
                                        </ContentBoxText>
                                    </Content>
                                </Col>
                                <Col
                                    lg={{ span: 5, order: 2 }}
                                    xs={{ span: 12, order: 2 }}
                                >
                                    <ContentImage>
                                        {it?.images[0]?.src && (
                                            <Image
                                                src={it.images[0].src}
                                                alt={
                                                    it.images[0].alt || it.title
                                                }
                                            />
                                        )}
                                    </ContentImage>
                                </Col>
                            </Row>
                        </ContentBox>
                    );
                } else {
                    return (
                        it.title == 'SDM-V1.2智能控制器' ?
                            <ContentBox>
                                <Container>
                                    {
                                        <Row>
                                            <Col
                                                lg={{ span: 7, order: 2 }}
                                                xs={{ span: 12, order: 2 }}
                                            >
                                                <Content style={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                                                    <ContentBoxTitle>
                                                        {it.title}
                                                    </ContentBoxTitle>
                                                    <ContentBoxText>
                                                        {it.description}
                                                    </ContentBoxText>
                                                </Content>
                                            </Col>
                                            <Col
                                                lg={{ span: 5, order: 1 }}
                                                xs={{ span: 12, order: 1 }}
                                            >
                                                <ContentImage>
                                                    {it?.images[0]?.src && (
                                                        <Image
                                                            src={it.images[0].src}
                                                            alt={
                                                                it.images[0].alt || it.title
                                                            }
                                                        />
                                                    )}
                                                </ContentImage>
                                            </Col>
                                        </Row>
                                    }
                                    <Row>
                                        <Col
                                            lg={{ span: 6, order: 1 }}
                                            xs={{ span: 12, order: 1 }}
                                        >
                                            <table style={{ borderCollapse: "collapse", width: "100%", margin: "10px 0" }}>
                                                {
                                                    customLeftTable.map((item) => <tr>
                                                        <td style={{ border: "1px solid #ddd", padding: 4, width: '30%' }}>{item.label}</td>
                                                        <td style={{ border: "1px solid #ddd", padding: 4, width: "70%" }}>{item.value}</td>
                                                    </tr>)
                                                }
                                            </table>
                                        </Col>
                                        <Col
                                            lg={{ span: 6, order: 1 }}
                                            xs={{ span: 12, order: 1 }}
                                        >
                                            <table style={{ borderCollapse: "collapse", width: "100%", margin: "10px 0" }}>
                                                {
                                                    customRightTable.map((item) => <tr>
                                                        <td style={{ border: "1px solid #ddd", padding: 4, width: '30%' }}>{item.label}</td>
                                                        <td style={{ border: "1px solid #ddd", padding: 4, width: "70%" }}>{item.value}</td>
                                                    </tr>)
                                                }
                                            </table>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col
                                            lg={{ span: 7, order: 2 }}
                                            xs={{ span: 12, order: 2 }}
                                            style={{ display: "flex" }}
                                        >
                                            <Content style={{ display: "flex", flex: 1, flexDirection: "column", justifyContent: "space-between" }}>
                                                {
                                                    customDescription.map((item) => <div style={{ marginBottom: "1rem" }}>
                                                        <div style={{ fontWeight: "bold", color: "#206daf" }}>{item.title}</div>
                                                        {item.text}
                                                    </div>)
                                                }
                                            </Content>
                                        </Col>
                                        <Col
                                            lg={{ span: 5, order: 1 }}
                                            xs={{ span: 12, order: 1 }}
                                        >
                                            <ContentImage>
                                                {it?.images?.map((item, index) => <div style={{ marginBottom: index == 4 ? 0 : '1rem' }}>

                                                    {
                                                        index !== 0 && <Image
                                                            src={item.src}
                                                            alt={
                                                                it.images[0].alt ||
                                                                it.title
                                                            }
                                                        />
                                                    }
                                                </div>)}
                                            </ContentImage>
                                        </Col>
                                    </Row>
                                </Container>
                            </ContentBox> :
                            <ContentBox>
                                <Container>
                                    <Row>
                                        <Col
                                            lg={{ span: 7, order: 2 }}
                                            xs={{ span: 12, order: 2 }}
                                        >
                                            <Content>
                                                <ContentBoxTitle>
                                                    {it.title}
                                                </ContentBoxTitle>
                                                <ContentBoxText>
                                                    {it.description}
                                                </ContentBoxText>
                                            </Content>
                                        </Col>
                                        <Col
                                            lg={{ span: 5, order: 1 }}
                                            xs={{ span: 12, order: 1 }}
                                        >
                                            <ContentImage>
                                                {it?.images[0]?.src && (
                                                    <Image
                                                        src={it.images[0].src}
                                                        alt={
                                                            it.images[0].alt ||
                                                            it.title
                                                        }
                                                    />
                                                )}
                                            </ContentImage>
                                        </Col>
                                    </Row>
                                </Container>
                            </ContentBox>
                    );
                }
            })}
        </SectionWrap>
    );
};

ContentBoxArea.propTypes = {
    data: PropTypes.shape({
        section_title: PropTypes.shape(SectionTitleType),
        items: PropTypes.arrayOf(PropTypes.shape(ItemType)),
    }),
};

ContentBoxArea.defaultProps = {
    layout: 0,
};
export default ContentBoxArea;
